import { z } from 'zod';
/**
 * CRM REST DTO — Phase 4 v1.1 Plan 04-02.
 *
 * 엔드포인트:
 *   - GET    /api/v1/crm/fans                            — cursor/filters/sort 팬 리스트
 *   - GET    /api/v1/crm/fans/:fanId                     — 단일 팬 프로필 (+ active sub)
 *   - GET    /api/v1/crm/fans/:fanId/timeline            — fan_activity_timeline MV 조회
 *   - GET    /api/v1/crm/segments                        — 세그먼트 목록 + 3 default templates
 *   - POST   /api/v1/crm/segments                        — 세그먼트 생성
 *   - PATCH  /api/v1/crm/segments/:id                    — 세그먼트 수정
 *   - DELETE /api/v1/crm/segments/:id                    — 세그먼트 삭제
 *   - GET    /api/v1/crm/segments/:id/fans               — 세그먼트 매칭 팬 (cursor)
 *   - POST   /api/v1/crm/segments/:id/match              — 강제 재계산 + 1 페이지 반환
 *
 * **경로**: `/api/v1/crm/*`. SessionMiddleware 가 req.user 주입. 각 controller 는
 * `req.user.id → streamers.user_id` 역조회로 streamerId 해상 (board.controller 패턴과 동일).
 *
 * **cursor 인코딩 (listFans)**:
 *   base64url(JSON.stringify({ ltv?:string, joinedAt?:string(ISO), id:string })).
 *   ltv 는 bigint → string, joinedAt 는 ISO-8601, id 는 uuid. service 는 decoded 객체 수용.
 *
 * **timeline cursor**:
 *   base64url(ISO-8601 timestamp). 서버 응답에 포함된 값을 다음 요청 query 로 그대로 사용.
 */
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
export const fanTierSchema = z.enum([
    'regular',
    'bronze',
    'silver',
    'gold',
    'vip',
]);
export const platformCodeSchema = z.enum(['chzzk', 'soop', 'youtube', 'twitch']);
export const segmentOperatorSchema = z.enum(['>=', '<=', '==']);
// ---------------------------------------------------------------------------
// listFans request / DTO
// ---------------------------------------------------------------------------
export const listFansQuerySchema = z.object({
    q: z.string().max(200).optional(),
    tier: fanTierSchema.optional(),
    ltvMin: z.coerce.bigint().optional(),
    ltvMax: z.coerce.bigint().optional(),
    lastActiveBefore: z.coerce.date().optional(),
    hasSubscription: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((v) => v === undefined ? undefined : v === true || v === 'true'),
    platform: platformCodeSchema.optional(),
    sort: z.enum(['ltvDesc', 'joinedAtDesc']).default('ltvDesc'),
    cursor: z.string().max(1024).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const fanListItemDtoSchema = z.object({
    streamerFanId: z.string().uuid(),
    fanId: z.string().uuid(),
    /** nullable — 스트리머 룸 내 닉 override */
    displayNick: z.string().nullable(),
    email: z.string(),
    /** users.name nullable */
    name: z.string().nullable(),
    tier: fanTierSchema,
    /** bigint → string wire */
    ltvKrw: z.string().regex(/^\d+$/),
    lastActiveAt: z.string().datetime({ offset: true }),
    firstDonatedAt: z.string().datetime({ offset: true }).nullable(),
    tags: z.array(z.string()),
    hasSubscription: z.boolean(),
    platforms: z.array(platformCodeSchema),
    createdAt: z.string().datetime({ offset: true }),
});
export const listFansResponseSchema = z.object({
    items: z.array(fanListItemDtoSchema),
    /** opaque base64url cursor. null 이면 마지막 페이지. */
    nextCursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// fan profile DTO
// ---------------------------------------------------------------------------
export const fanActiveSubscriptionSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(['active', 'past_due', 'paused', 'canceled']),
    tierId: z.string().uuid(),
    tierName: z.string(),
    /** bigint → string wire */
    tierPriceKrw: z.string().regex(/^\d+$/),
    startedAt: z.string().datetime({ offset: true }),
    currentPeriodEnd: z.string().datetime({ offset: true }),
    nextBillingAt: z.string().datetime({ offset: true }),
});
export const fanPlatformConnectionSchema = z.object({
    platform: platformCodeSchema,
    externalHandle: z.string().nullable(),
});
export const fanProfileDtoSchema = z.object({
    streamerFanId: z.string().uuid(),
    fanId: z.string().uuid(),
    streamerId: z.string().uuid(),
    displayNick: z.string().nullable(),
    email: z.string(),
    name: z.string().nullable(),
    slug: z.string(),
    /** PII 최소화 — 전화번호는 응답에 포함, UI 에서만 표시 */
    phone: z.string().nullable(),
    tier: fanTierSchema,
    ltvKrw: z.string().regex(/^\d+$/),
    lastActiveAt: z.string().datetime({ offset: true }),
    firstDonatedAt: z.string().datetime({ offset: true }).nullable(),
    tags: z.array(z.string()),
    badges: z.array(z.string()),
    createdAt: z.string().datetime({ offset: true }),
    activeSubscription: fanActiveSubscriptionSchema.nullable(),
    platforms: z.array(fanPlatformConnectionSchema),
});
// ---------------------------------------------------------------------------
// timeline DTO
// ---------------------------------------------------------------------------
export const timelineKindSchema = z.enum([
    'donation',
    'subscription_renewal',
    'post',
    'comment',
    'ama_question',
]);
export const timelineItemDtoSchema = z.object({
    kind: timelineKindSchema,
    occurredAt: z.string().datetime({ offset: true }),
    sourceId: z.string().uuid(),
    /** donation · subscription_renewal 만 값, 나머지 null. bigint → string. */
    amountKrw: z
        .string()
        .regex(/^\d+$/)
        .nullable(),
    subId: z.string().uuid().nullable(),
    postId: z.string().uuid().nullable(),
    summary: z.string().nullable(),
});
export const getTimelineQuerySchema = z.object({
    cursor: z.string().datetime({ offset: true }).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const getTimelineResponseSchema = z.object({
    items: z.array(timelineItemDtoSchema),
    /** ISO-8601 timestamp 또는 null */
    nextCursor: z.string().datetime({ offset: true }).nullable(),
});
// ---------------------------------------------------------------------------
// segment DSL (RE-EXPORT of apps/api internal schema shape for UI reuse)
// ---------------------------------------------------------------------------
/**
 * 본 Zod 는 apps/api/src/modules/crm/segment-dsl.schema.ts 와 **구조적으로 동일**.
 * contracts 는 @loop/db 의존을 회피해야 하므로 별도 선언 — apps/web UI 가 JSON editor
 * 검증에 바로 사용. apps/api 는 내부 schema 를 사용 (runtime 검증은 양쪽 동일 결과).
 */
export const segmentConditionSchema = z.discriminatedUnion('field', [
    z.object({
        field: z.literal('ltv_krw'),
        operator: segmentOperatorSchema,
        value: z.number().int().min(0).max(100_000_000),
    }).strict(),
    z.object({
        field: z.literal('tier'),
        operator: z.literal('=='),
        value: fanTierSchema,
    }).strict(),
    z.object({
        field: z.literal('last_active_days_ago'),
        operator: z.enum(['>=', '<=']),
        value: z.number().int().min(0).max(3650),
    }).strict(),
    z.object({
        field: z.literal('has_subscription'),
        operator: z.literal('=='),
        value: z.boolean(),
    }).strict(),
    z.object({
        field: z.literal('joined_days_ago'),
        operator: z.enum(['>=', '<=']),
        value: z.number().int().min(0).max(3650),
    }).strict(),
    z.object({
        field: z.literal('platform'),
        operator: z.literal('=='),
        value: platformCodeSchema,
    }).strict(),
]);
export const segmentConditionsDslSchema = z
    .object({
    version: z.literal(1),
    logic: z.enum(['AND', 'OR']),
    conditions: z.array(segmentConditionSchema).min(1).max(10),
})
    .strict();
// ---------------------------------------------------------------------------
// segment DTO + requests
// ---------------------------------------------------------------------------
export const segmentDtoSchema = z.object({
    id: z.string().uuid(),
    streamerId: z.string().uuid(),
    name: z.string().min(1).max(100),
    description: z.string().max(500).nullable(),
    conditions: segmentConditionsDslSchema,
    matchedCount: z.number().int().min(0),
    lastComputedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const segmentTemplateDtoSchema = z.object({
    key: z.enum(['vip', 'at_risk', 'new']),
    name: z.string(),
    description: z.string(),
    conditions: segmentConditionsDslSchema,
});
export const createSegmentRequestSchema = z.object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500).optional().nullable(),
    conditions: segmentConditionsDslSchema,
});
export const updateSegmentRequestSchema = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    description: z.string().trim().max(500).optional().nullable(),
    conditions: segmentConditionsDslSchema.optional(),
});
export const listSegmentsResponseSchema = z.object({
    items: z.array(segmentDtoSchema),
    templates: z.array(segmentTemplateDtoSchema),
});
export const matchSegmentQuerySchema = z.object({
    cursor: z.string().max(1024).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const matchSegmentResponseSchema = z.object({
    items: z.array(fanListItemDtoSchema),
    nextCursor: z.string().nullable(),
    matchedCount: z.number().int().min(0),
});
//# sourceMappingURL=crm.rest.js.map